// kanjo/Lock.jsx — device-lock UX: enrollment, unlock, and a settings panel.
// Backed by window.kanjo.lock (app/src/lib/lock/deviceLock.js). See
// docs/LOCAL_LOCK_DESIGN.md. SPIKE: validates the unlock lifecycle + UX; the
// master key is produced for real but not yet wired to data-at-rest.

function LockSpinner() {
  return (
    <svg className="kanjo-spinner" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="14 30" opacity="0.7"/>
      <style>{`.kanjo-spinner { transform-origin: 50% 50%; animation: kanjo-spin 0.9s linear infinite; }
@keyframes kanjo-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

function FingerprintMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 11v2a9 9 0 0 1-1.5 5"/>
      <path d="M7 11a5 5 0 0 1 10 0v1a16 16 0 0 0 .9 5"/>
      <path d="M4.5 9a8 8 0 0 1 14.5-1.5"/>
      <path d="M9.5 11a2.5 2.5 0 0 1 5 0v2a20 20 0 0 0 .6 5"/>
    </svg>
  );
}

function FieldNote({ children, tone = 'muted' }) {
  const color = tone === 'danger' ? 'var(--danger)' : tone === 'warn' ? 'var(--ink-2)' : 'var(--on-dim)';
  const bg = tone === 'danger' ? 'var(--danger-soft)' : tone === 'warn' ? 'var(--brand-soft)' : 'transparent';
  return (
    <div role={tone === 'danger' ? 'alert' : undefined}
         style={{ fontSize: 13, lineHeight: 1.45, color, background: bg,
                  padding: bg === 'transparent' ? '6px 0 0' : '12px 14px',
                  borderRadius: bg === 'transparent' ? 0 : 12, marginBottom: 12 }}>
      {children}
    </div>
  );
}

/** The code itself plus its copy affordance — shown at enrollment and again
 *  after a rotation, so the two places can never drift apart. */
function RecoveryCode({ code }) {
  const [copied, setCopied] = React.useState(false);
  // A new code resets the affordance: "✓ Copied" left over from the previous
  // one would claim the owner has saved a code they have not seen.
  React.useEffect(() => { setCopied(false); }, [code]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); } catch { /* clipboard blocked */ }
  };
  return (
    <>
      <div style={{ fontFamily: 'var(--mono, ui-monospace, monospace)', fontSize: 22, letterSpacing: '0.06em',
                    textAlign: 'center', padding: '16px 12px', borderRadius: 12,
                    background: 'var(--surface-3)', color: 'var(--ink)', userSelect: 'all', marginBottom: 12 }}>
        {code}
      </div>
      <button className="btn ghost" onClick={copy} aria-label="Copy recovery code">
        {copied ? '✓ Copied' : 'Copy code'}
      </button>
    </>
  );
}

const lockInput = {
  width: '100%', boxSizing: 'border-box', padding: '13px 14px',
  borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--ink)', fontSize: 16, marginBottom: 10, // 16px avoids iOS zoom-on-focus
};

// ── Enrollment ───────────────────────────────────────────────────────────────

function EnrollScreen({ onComplete, dark = false }) {
  const bioAvailable = (() => { try { return window.kanjo.lock.isWebauthnAvailable(); } catch { return false; } })();
  // 'choose' (biometric-first), 'passphrase' (chose to set their own), 'showcode'
  // (post-enroll: display the generated recovery code).
  const [mode, setMode] = React.useState(bioAvailable ? 'choose' : 'passphrase');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [pass, setPass] = React.useState('');
  const [showPass, setShowPass] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [bioOn, setBioOn] = React.useState(false);

  // Primary path: one biometric tap. We generate the recovery code for them and
  // enroll with biometric + that code, then reveal the code to save.
  const startBiometric = async () => {
    setError(null); setBusy(true);
    try {
      const rc = window.kanjo.lock.generateRecoveryCode();
      const r = await window.kanjo.lock.enroll({ passphrase: rc, useBiometric: true });
      setCode(rc); setBioOn(!!r.biometricEnabled); setMode('showcode');
    } catch (e) {
      setError(e && e.message ? e.message : 'Could not set up the lock.');
    } finally { setBusy(false); }
  };

  // Secondary path: the user sets their own passphrase (e.g. declines biometric).
  const submitPassphrase = async () => {
    setError(null);
    if (pass.length < 8) { setError('Use at least 8 characters.'); return; }
    setBusy(true);
    try {
      await window.kanjo.lock.enroll({ passphrase: pass, useBiometric: false });
      setPass('');
      onComplete({ enrolled: true });
    } catch (e) {
      setError(e && e.message ? e.message : 'Could not set up the lock.');
    } finally { setBusy(false); }
  };

  // ── Step 2: reveal the generated recovery code ────────────────────────────
  if (mode === 'showcode') {
    return (
      <KShell dark={dark} tab="today" noNav={true}>
        <div style={shellPad}>
          <div style={{ marginTop: 8 }}>
            <Lockmark/>
            <h1 style={titleStyle}>{bioOn ? 'You’re protected' : 'Save your recovery code'}</h1>
            <p style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, marginBottom: 18 }}>
              {bioOn
                ? 'Unlock with Face ID or your fingerprint. Save this recovery code — you’ll only need it to set up a new phone.'
                : 'Biometric unlock isn’t available here, so this recovery code is how you’ll unlock. Save it somewhere safe.'}
            </p>
            <RecoveryCode code={code}/>
            <FieldNote>We can’t reset or recover this for you — if it’s lost and you change phones, your books can’t be restored. You can replace it any time from Account › Security.</FieldNote>
          </div>
          <button className="btn lg" style={primaryBtn} onClick={() => onComplete({ enrolled: true })}>
            I’ve saved it
          </button>
        </div>
      </KShell>
    );
  }

  // ── Step 1 ────────────────────────────────────────────────────────────────
  return (
    <KShell dark={dark} tab="today" noNav={true}>
      <div style={shellPad}>
        <div style={{ marginTop: 8 }}>
          <Lockmark/>
          <h1 style={titleStyle}>Protect this device</h1>
          <p style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, marginBottom: 22, maxWidth: 340 }}>
            Your financial data stays encrypted on this phone.
          </p>
          {error && <FieldNote tone="danger">{error}</FieldNote>}

          {mode === 'passphrase' && (
            <>
              <label style={labelStyle}>Recovery passphrase</label>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'} autoComplete="new-password" inputMode="text"
                       placeholder="At least 8 characters" value={pass} style={{ ...lockInput, paddingRight: 64 }}
                       onChange={(e) => setPass(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') submitPassphrase(); }}
                       aria-label="Recovery passphrase"/>
                <button type="button" onClick={() => setShowPass((v) => !v)} aria-label={showPass ? 'Hide' : 'Show'}
                        style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none',
                                 color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer', padding: 4 }}>
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
              <FieldNote>Keep it safe — it’s the only way to recover your books on a new phone, and we can’t reset it for you.</FieldNote>
            </>
          )}
        </div>

        <div>
          {mode === 'choose' ? (
            <>
              <button className="btn lg" style={{ ...primaryBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, opacity: busy ? 0.6 : 1 }}
                      disabled={busy} onClick={startBiometric}>
                {busy ? <LockSpinner/> : <><FingerprintMark/> Use Face ID or fingerprint</>}
              </button>
              <button className="btn ghost lg" style={{ width: '100%', marginTop: 10 }} disabled={busy}
                      onClick={() => { setError(null); setMode('passphrase'); }}>
                Use a passphrase instead
              </button>
            </>
          ) : (
            <button className="btn lg" style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submitPassphrase}>
              {busy ? <LockSpinner/> : 'Set up lock'}
            </button>
          )}
          <button className="btn ghost lg" style={{ width: '100%', marginTop: 10 }} disabled={busy}
                  onClick={() => onComplete({ skipped: true })}>
            Not now
          </button>
        </div>
      </div>
    </KShell>
  );
}

// ── Unlock ───────────────────────────────────────────────────────────────────

function UnlockScreen({ hasBiometric, onUnlocked, dark = false }) {
  const [mode, setMode] = React.useState(hasBiometric ? 'biometric' : 'passphrase');
  const [pass, setPass] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [showInfo, setShowInfo] = React.useState(false);

  const tryBiometric = async () => {
    setError(null); setBusy(true);
    try {
      const ok = await window.kanjo.lock.unlockWithBiometric();
      if (ok) { onUnlocked(); return; }
      setError('Couldn’t unlock. Try your passphrase.');
      setMode('passphrase');
    } catch (e) {
      const code = e && e.code;
      if (code === 'PRF_UNSUPPORTED' || code === 'NO_BIOMETRIC' || code === 'NO_WEBAUTHN') {
        setMode('passphrase');
      } else {
        setError('Biometric unlock was cancelled or failed. Use your passphrase.');
        setMode('passphrase');
      }
    } finally {
      setBusy(false);
    }
  };

  const tryPassphrase = async () => {
    setError(null);
    if (!pass) { setError('Enter your passphrase.'); return; }
    setBusy(true);
    try {
      const ok = await window.kanjo.lock.unlockWithPassphrase(pass);
      if (ok) { setPass(''); onUnlocked(); return; }
      setError('That passphrase didn’t work.');
    } catch (e) {
      setError(e && e.message ? e.message : 'Could not unlock.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <KShell dark={dark} tab="today" noNav={true}>
      <div style={shellPad}>
        <div style={{ marginTop: 8 }}>
          <Lockmark/>
          <h1 style={titleStyle}>Welcome back</h1>
          <p style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, marginBottom: 22 }}>
            Unlock to open your books on this device.
          </p>
          {error && <FieldNote tone="danger">{error}</FieldNote>}

          {mode === 'passphrase' && (
            <>
              <label style={labelStyle}>Recovery code or passphrase</label>
              <input type="password" autoComplete="current-password" placeholder="Recovery code or passphrase"
                     value={pass} style={lockInput} onChange={(e) => setPass(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') tryPassphrase(); }} aria-label="Recovery code or passphrase"/>
            </>
          )}
        </div>

        <div>
          {mode === 'biometric' ? (
            <>
              <button className="btn lg" style={{ ...primaryBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, opacity: busy ? 0.6 : 1 }}
                      disabled={busy} onClick={tryBiometric}>
                {busy ? <LockSpinner/> : <><FingerprintMark/> Unlock with Face ID or fingerprint</>}
              </button>
              <button type="button" style={{ ...fallbackLink, opacity: busy ? 0.6 : 1 }} disabled={busy}
                      onClick={() => { setError(null); setMode('passphrase'); }}>
                Use passphrase instead
              </button>
            </>
          ) : (
            <>
              <button className="btn lg" style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={tryPassphrase}>
                {busy ? <LockSpinner/> : 'Unlock'}
              </button>
              {hasBiometric && (
                <button type="button" style={{ ...fallbackLink, opacity: busy ? 0.6 : 1 }} disabled={busy}
                        onClick={() => { setError(null); setMode('biometric'); }}>
                  Use Face ID or fingerprint
                </button>
              )}
            </>
          )}

          {/* Same notice as the login screen. This is the screen a returning
              operator actually sees — the login screen is a once-a-year event
              after signup — so without it here the statement of what these
              numbers are would be effectively unreachable. Kept to a quiet
              line: it has to be findable every day, not shouted every day. */}
          <div style={{ marginTop: 22, textAlign: 'center' }}>
            <button type="button" onClick={() => setShowInfo(true)}
              style={{ background: 'transparent', border: 'none', padding: '10px 8px', minHeight: 44,
                color: 'var(--ink-3)', fontSize: 12, cursor: 'pointer', lineHeight: 1.5 }}>
              Kanjo is not accounting software
            </button>
          </div>
        </div>
      </div>

      {/* createElement, not <window.DisclaimerInfo/> — same cross-bundle
          pattern Today.jsx uses for CategorySelect. */}
      {showInfo && window.DisclaimerInfo
        && React.createElement(window.DisclaimerInfo, { onClose: () => setShowInfo(false) })}
    </KShell>
  );
}

// ── Books-recovery gate (new device, encrypted snapshot) ─────────────────────
// Rendered by main.jsx when rehydrateIfNeeded reports needsPhrase: the cloud
// books are client-encrypted and this device's key can't open them. The phrase
// unwraps the escrowed crypto salt (backup.recoverWithPhrase), after which the
// caller re-runs the rehydrate. Skipping starts with empty local books — the
// encrypted blob stays untouched in S3, so nothing is lost by skipping.
function BooksRecoveryGate({ onRecovered, onSkip, dark = false }) {
  const [phrase, setPhrase] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const tryRecover = async () => {
    setError(null);
    if (!phrase.trim()) { setError('Enter your recovery phrase.'); return; }
    setBusy(true);
    try {
      await window.kanjo.backup.recoverWithPhrase(phrase, { force: true });
      setPhrase('');
      onRecovered();
    } catch (e) {
      setError(/wrong recovery phrase/i.test(String(e?.message)) ? 'That recovery phrase didn’t work.' : (e?.message || 'Recovery failed.'));
    } finally { setBusy(false); }
  };

  return (
    <KShell dark={dark} tab="today" noNav={true}>
      <div style={shellPad}>
        <div style={{ marginTop: 8 }}>
          <Lockmark/>
          <h1 style={titleStyle}>Your books are encrypted</h1>
          <p style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, marginBottom: 22 }}>
            This is a new device. Enter the recovery phrase you set when you turned on
            book encryption, and your books will be restored here.
          </p>
          {error && <FieldNote tone="danger">{error}</FieldNote>}
          <label style={labelStyle}>Recovery phrase</label>
          <input type="password" autoComplete="off" placeholder="Recovery phrase"
                 value={phrase} style={lockInput} onChange={(e) => setPhrase(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') tryRecover(); }} aria-label="Recovery phrase"/>
        </div>
        <div>
          <button className="btn lg" style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={tryRecover}>
            {busy ? <LockSpinner/> : 'Restore my books'}
          </button>
          <button className="btn ghost lg" style={{ width: '100%', marginTop: 10 }} disabled={busy} onClick={onSkip}>
            Skip for now — start empty on this device
          </button>
        </div>
      </div>
    </KShell>
  );
}

// ── Settings panel (embed in Account) ─────────────────────────────────────────

function SecurityPanel() {
  const [state, setState] = React.useState(null); // { enrolled, hasBiometric }
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [askPass, setAskPass] = React.useState(false);
  const [pass, setPass] = React.useState('');
  // Recovery-code rotation: null → 'confirm' (prove access) → 'show' (the new
  // code, once). `rotatePass` is kept apart from `pass` so cancelling one flow
  // cannot leave a secret sitting in the other's field.
  const [rotate, setRotate] = React.useState(null);
  const [rotatePass, setRotatePass] = React.useState('');
  const [newCode, setNewCode] = React.useState('');

  const refresh = React.useCallback(async () => {
    const L = window.kanjo.lock;
    setState({ enrolled: await L.isEnrolled(), hasBiometric: await L.hasBiometric() });
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  if (!state) return null;

  const enableBiometric = async () => {
    setMsg(null);
    if (!pass) { setMsg({ tone: 'danger', text: 'Enter your passphrase to confirm.' }); return; }
    setBusy(true);
    try {
      const r = await window.kanjo.lock.addBiometric(pass);
      setPass(''); setAskPass(false);
      setMsg(r.biometricEnabled ? { tone: 'ok', text: 'Biometric unlock enabled.' } : { tone: 'warn', text: r.prfWarning });
      await refresh();
    } catch (e) {
      setMsg({ tone: 'danger', text: e && e.message ? e.message : 'Could not enable biometrics.' });
    } finally { setBusy(false); }
  };

  const lockNow = () => { window.kanjo.lock.lock(); window.location.reload(); };

  // Mint a replacement recovery code. `viaBiometric` runs a Face ID ceremony
  // instead of asking for the code the owner is here because they lost.
  const doRotate = async (viaBiometric) => {
    setMsg(null);
    if (!viaBiometric && !rotatePass) {
      setMsg({ tone: 'danger', text: 'Enter your current code or passphrase.' });
      return;
    }
    setBusy(true);
    try {
      const r = await window.kanjo.lock.rotateRecovery(
        viaBiometric ? { viaBiometric: true } : { current: rotatePass });
      setRotatePass(''); setNewCode(r.code); setRotate('show');
      await refresh();
    } catch (e) {
      const m = String((e && e.message) || '');
      setMsg({ tone: 'danger', text: /incorrect/.test(m) ? 'That code or passphrase didn’t work.'
        : /biometric/i.test(m) ? 'Biometric check failed. Use your current code instead.'
        : 'Could not replace the recovery code.' });
    } finally { setBusy(false); }
  };

  const closeRotate = () => { setRotate(null); setRotatePass(''); setNewCode(''); setMsg(null); };

  const turnOff = async () => {
    if (!window.confirm('Turn off the lock on this device? Your books stay on this device; you’ll no longer be asked to unlock.')) return;
    setBusy(true);
    try { await window.kanjo.lock.disable(); setMsg({ tone: 'ok', text: 'Lock turned off.' }); await refresh(); }
    catch (e) { setMsg({ tone: 'danger', text: e && e.message ? e.message : 'Could not turn off the lock.' }); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
        {state.enrolled
          ? (state.hasBiometric ? 'This device is protected. Biometric unlock is on.' : 'This device is protected by your passphrase.')
          : 'This device is not protected yet.'}
      </div>
      {msg && <FieldNote tone={msg.tone === 'danger' ? 'danger' : 'warn'}>{msg.text}</FieldNote>}

      {state.enrolled && !state.hasBiometric && !askPass && rotate === null && (
        <button className="btn" style={{ marginBottom: 10 }} onClick={() => { setMsg(null); setAskPass(true); }}>
          Enable Face ID / fingerprint
        </button>
      )}
      {askPass && (
        <div style={{ marginBottom: 10 }}>
          <input type="password" autoComplete="current-password" placeholder="Confirm passphrase"
                 value={pass} style={lockInput} onChange={(e) => setPass(e.target.value)} aria-label="Confirm passphrase"/>
          <button className="btn" disabled={busy} onClick={enableBiometric}>{busy ? <LockSpinner/> : 'Enable biometric unlock'}</button>
          <button className="btn ghost" style={{ marginLeft: 8 }} disabled={busy} onClick={() => { setAskPass(false); setPass(''); }}>Cancel</button>
        </div>
      )}
      {/* ── Recovery code ─────────────────────────────────────────────────── */}
      {state.enrolled && rotate === null && (
        <div style={{ marginBottom: 10 }}>
          <button className="btn" onClick={() => { setMsg(null); setRotate('confirm'); }}>
            Replace recovery code
          </button>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 6, lineHeight: 1.45 }}>
            Issues a new code and retires the old one. Your books are re-locked under the
            same key, so nothing needs re-encrypting.
          </div>
        </div>
      )}

      {rotate === 'confirm' && (
        <div style={{ marginBottom: 10 }}>
          {state.hasBiometric && (
            <button className="btn" style={{ marginBottom: 10 }} disabled={busy} onClick={() => doRotate(true)}>
              {busy ? <LockSpinner/> : <><FingerprintMark/> Confirm with Face ID</>}
            </button>
          )}
          <input type="password" autoComplete="current-password" placeholder="Current recovery code or passphrase"
                 value={rotatePass} style={lockInput} onChange={(e) => setRotatePass(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') doRotate(false); }}
                 aria-label="Current recovery code or passphrase"/>
          <button className="btn" disabled={busy} onClick={() => doRotate(false)}>
            {busy ? <LockSpinner/> : 'Issue a new code'}
          </button>
          <button className="btn ghost" style={{ marginLeft: 8 }} disabled={busy} onClick={closeRotate}>Cancel</button>
        </div>
      )}

      {rotate === 'show' && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 10 }}>
            Your new recovery code. Save it now — this is the only time it’s shown, and
            your previous code no longer works.
          </div>
          <RecoveryCode code={newCode}/>
          <button className="btn" style={{ marginTop: 10 }} onClick={closeRotate}>I’ve saved it</button>
        </div>
      )}

      {state.enrolled && rotate === null && (
        <>
          <button className="btn ghost" onClick={lockNow}>Lock now</button>
          <button className="btn ghost" style={{ marginLeft: 8, color: 'var(--danger)' }} disabled={busy} onClick={turnOff}>Turn off lock</button>
        </>
      )}
    </div>
  );
}

// ── shared bits ────────────────────────────────────────────────────────────

const shellPad = { height: '100%', display: 'flex', flexDirection: 'column', padding: '40px 28px 32px', justifyContent: 'space-between' };
const titleStyle = { fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', margin: '18px 0 8px', color: 'var(--on)' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--on-soft)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 };
const primaryBtn = { width: '100%', background: 'var(--brand-fill)', color: 'var(--on-brand-fill)' };
// The other way in (passphrase when biometric is offered, and vice versa).
// Deliberately NOT a second full-width slab 10px under the primary one: two
// identical buttons that close together invite a mis-tap, and on the unlock
// screen a mis-tap costs you the Face ID prompt and drops you into a passphrase
// field instead. A centred text link, set well below, reads as the escape hatch
// it is — while keeping a 44px tap target.
const fallbackLink = {
  display: 'block', width: 'auto', margin: '30px auto 0', padding: '12px 16px', minHeight: 44,
  background: 'transparent', border: 'none', color: 'var(--ink-2)',
  fontSize: 14, fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: 3,
  cursor: 'pointer',
};

function Lockmark() {
  return (
    <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="11" width="16" height="9" rx="2"/>
        <path d="M8 11V8a4 4 0 0 1 8 0v3"/>
      </svg>
    </div>
  );
}

Object.assign(window, { EnrollScreen, UnlockScreen, SecurityPanel, BooksRecoveryGate });
